import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ToneBadge } from "./StatusBadge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { DIAGNOSIS_TYPES, DIAGNOSIS_STATUSES } from "@/lib/constants";
import { Stethoscope, Play, CheckCircle2, Plus } from "lucide-react";

export function DiagnosisPanel({ serviceOrder, diagnoses, users, onChanged }) {
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // full obj
  const empty = {
    assigned_user_id: "",
    diagnosis_type: "mechanical",
    preliminary_findings: "",
    diagnostic_tests: "",
    fault_codes: "",
    final_findings: "",
    technical_recommendation: "",
    customer_notes: "",
    customer_authorization_required: false,
  };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  function openCreate() {
    setEditing(null);
    setForm(empty);
    setCreating(true);
  }
  function openEdit(d) {
    setEditing(d);
    setForm({ ...empty, ...d });
    setCreating(true);
  }

  async function save() {
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/service-orders/${serviceOrder.id}/diagnoses/${editing.id}`, form);
        toast.success("Diagnóstico actualizado");
      } else {
        await api.post(`/service-orders/${serviceOrder.id}/diagnoses`, form);
        toast.success("Diagnóstico creado");
      }
      setCreating(false);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  async function markCompleted(d) {
    try {
      await api.patch(`/service-orders/${serviceOrder.id}/diagnoses/${d.id}`, { status: "completed" });
      toast.success("Diagnóstico completado");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  return (
    <div className="space-y-4" data-testid="panel-diagnoses">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg tracking-tight">Diagnósticos</h3>
          <p className="text-xs text-muted-foreground">
            Hallazgos, pruebas y recomendaciones técnicas del caso.
          </p>
        </div>
        {!creating && (
          <Button className="btn-tenant" size="sm" onClick={openCreate} data-testid="btn-new-diagnosis">
            <Plus className="h-4 w-4 mr-2" /> Nuevo diagnóstico
          </Button>
        )}
      </div>

      {creating && (
        <Card className="p-5 border-border space-y-4" data-testid="diagnosis-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Tipo de diagnóstico</Label>
              <Select value={form.diagnosis_type} onValueChange={(v) => setForm({ ...form, diagnosis_type: v })}>
                <SelectTrigger data-testid="diagnosis-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DIAGNOSIS_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Responsable</Label>
              <Select value={form.assigned_user_id} onValueChange={(v) => setForm({ ...form, assigned_user_id: v })}>
                <SelectTrigger data-testid="diagnosis-assignee"><SelectValue placeholder="Técnico o usuario" /></SelectTrigger>
                <SelectContent>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Hallazgos preliminares</Label>
            <Textarea rows={2} value={form.preliminary_findings} onChange={(e) => setForm({ ...form, preliminary_findings: e.target.value })} data-testid="diagnosis-preliminary" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Pruebas realizadas</Label>
              <Textarea rows={2} value={form.diagnostic_tests} onChange={(e) => setForm({ ...form, diagnostic_tests: e.target.value })} data-testid="diagnosis-tests" />
            </div>
            <div className="space-y-2">
              <Label>Códigos de falla (DTC)</Label>
              <Input value={form.fault_codes} onChange={(e) => setForm({ ...form, fault_codes: e.target.value.toUpperCase() })} placeholder="P0301, P0420…" className="mono" data-testid="diagnosis-codes" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Hallazgos finales</Label>
            <Textarea rows={2} value={form.final_findings} onChange={(e) => setForm({ ...form, final_findings: e.target.value })} data-testid="diagnosis-final" />
          </div>
          <div className="space-y-2">
            <Label>Recomendación técnica</Label>
            <Textarea rows={2} value={form.technical_recommendation} onChange={(e) => setForm({ ...form, technical_recommendation: e.target.value })} data-testid="diagnosis-recommendation" />
          </div>
          <div className="space-y-2">
            <Label>Notas para el cliente (se ven en su portal)</Label>
            <Textarea rows={2} value={form.customer_notes} onChange={(e) => setForm({ ...form, customer_notes: e.target.value })} data-testid="diagnosis-customer-notes" />
          </div>
          <div className="flex items-center justify-between p-3 rounded-md border border-border">
            <div>
              <Label htmlFor="cust_auth">Requiere autorización del cliente</Label>
              <p className="text-xs text-muted-foreground">Marcar cuando el trabajo debe validarse antes de comenzar.</p>
            </div>
            <Switch
              id="cust_auth"
              checked={form.customer_authorization_required}
              onCheckedChange={(v) => setForm({ ...form, customer_authorization_required: v })}
              data-testid="diagnosis-auth-required"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setCreating(false)} data-testid="diagnosis-cancel">Cancelar</Button>
            <Button onClick={save} disabled={saving} className="btn-tenant" data-testid="diagnosis-save">
              {saving ? "Guardando…" : editing ? "Guardar cambios" : "Crear diagnóstico"}
            </Button>
          </div>
        </Card>
      )}

      {diagnoses.length === 0 && !creating && (
        <div className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-8 text-center bg-white">
          <Stethoscope className="h-6 w-6 mx-auto mb-2 text-slate-300" />
          Aún no hay diagnósticos para este folio.
        </div>
      )}

      <div className="space-y-3">
        {diagnoses.map((d) => (
          <Card key={d.id} className="border-border p-4" data-testid={`diagnosis-${d.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <ToneBadge list={DIAGNOSIS_STATUSES} value={d.status} />
                  {d.customer_authorization_required && (
                    <span className="text-[11px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                      Requiere autorización
                    </span>
                  )}
                </div>
                <div className="text-sm text-muted-foreground">
                  {DIAGNOSIS_TYPES.find((t) => t.value === d.diagnosis_type)?.label || d.diagnosis_type}
                  {d.started_at && ` · Iniciado ${d.started_at.slice(0, 10)}`}
                  {d.completed_at && ` · Completado ${d.completed_at.slice(0, 10)}`}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {d.status !== "completed" && (
                  <Button variant="outline" size="sm" onClick={() => markCompleted(d)} data-testid={`diagnosis-complete-${d.id}`}>
                    <CheckCircle2 className="h-4 w-4 mr-1" /> Marcar completado
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => openEdit(d)} data-testid={`diagnosis-edit-${d.id}`}>
                  Editar
                </Button>
              </div>
            </div>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              {d.preliminary_findings && <div><dt className="text-[11px] uppercase text-muted-foreground tracking-wider">Preliminares</dt><dd>{d.preliminary_findings}</dd></div>}
              {d.diagnostic_tests && <div><dt className="text-[11px] uppercase text-muted-foreground tracking-wider">Pruebas</dt><dd>{d.diagnostic_tests}</dd></div>}
              {d.fault_codes && <div><dt className="text-[11px] uppercase text-muted-foreground tracking-wider">Códigos DTC</dt><dd className="mono">{d.fault_codes}</dd></div>}
              {d.final_findings && <div><dt className="text-[11px] uppercase text-muted-foreground tracking-wider">Hallazgos finales</dt><dd>{d.final_findings}</dd></div>}
              {d.customer_notes && <div className="md:col-span-2"><dt className="text-[11px] uppercase text-muted-foreground tracking-wider">Notas para el cliente</dt><dd>{d.customer_notes}</dd></div>}
              {d.technical_recommendation && <div className="md:col-span-2"><dt className="text-[11px] uppercase text-muted-foreground tracking-wider">Recomendación técnica</dt><dd>{d.technical_recommendation}</dd></div>}
            </dl>
          </Card>
        ))}
      </div>
    </div>
  );
}
